// İSG uzmanı portalı — Kontrol aşaması departman onay/ret aksiyonları (api-contract §31.4). Çağıran
// seçili departmanı adına **Onayla** (`.../department-review/approve {department, note?}`) veya
// **Reddet** (`.../department-review/reject {department, reason}` → izin Revision'a; **reason zorunlu**
// modal). Departman-rol kapısı backend'dedir (yetkisiz → `403 DEPARTMENT_FORBIDDEN` — kullanıcı-dostu
// mesaj olarak yansır; buton zaten kullanıcının kendi departmanına kilitli). Kendi departmanı zaten
// onayladıysa Onayla gizlenir (tekrar onay anlamsız). Her aksiyon sonrası Kontrol detayı + kuyruk +
// izin listesi invalidate edilir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  ConfirmDialog,
  Field,
  Modal,
  type PermitApprovalDepartment,
  type PermitDepartmentApprovalStatus,
  Spinner,
  Textarea,
  departmentReviewApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { departmentReviewKeys } from '../../lib/departmentReviewKeys';
import { permitsKeys } from '../../lib/permitsKeys';

interface DepartmentReviewActionsProps {
  permitUuid: string;
  department: PermitApprovalDepartment;
  /** Çağıranın kendi departmanının bu izindeki güncel durumu (varsa). */
  viewerStatus: PermitDepartmentApprovalStatus | undefined;
}

export function DepartmentReviewActions({
  permitUuid,
  department,
  viewerStatus,
}: DepartmentReviewActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  function invalidate() {
    // Kontrol detay + kuyruk (departman onay katmanı) + izin listesi (durum değişmiş olabilir).
    queryClient.invalidateQueries({ queryKey: departmentReviewKeys.all() });
    queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
  }

  const approveMutation = useMutation({
    mutationFn: () =>
      departmentReviewApi.approveDepartmentReview(permitUuid, { department: department }),
    onSuccess: () => {
      invalidate();
      setApproveOpen(false);
    },
  });

  // Kendi departmanı zaten onayladıysa Onayla gizlenir; Reddet açık kalır (karar değişikliği
  // backend kararıdır — yetkisizse 403 döner). Reddedilmişse izin Revision'a düşmüştür (buton yine
  // status'a göre backend tarafından kapılır).
  const alreadyApproved = viewerStatus === 'Approved';

  return (
    <div className="flex flex-wrap items-center gap-2">
      {alreadyApproved ? null : (
        <Button variant="primary" size="sm" onClick={() => setApproveOpen(true)}>
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {t('departmentReview.action.approve')}
        </Button>
      )}
      <Button variant="danger" size="sm" onClick={() => setRejectOpen(true)}>
        <XCircle className="h-4 w-4" aria-hidden="true" />
        {t('departmentReview.action.reject')}
      </Button>

      <ConfirmDialog
        open={approveOpen}
        onOpenChange={(open) => {
          if (!open) {
            approveMutation.reset();
          }
          setApproveOpen(open);
        }}
        title={t('departmentReview.approve.title')}
        body={t('departmentReview.approve.body', {
          department: t(`permits.department.${department}`),
        })}
        confirmLabel={t('departmentReview.approve.confirm')}
        confirmVariant="primary"
        loading={approveMutation.isPending}
        error={approveMutation.isError ? approveMutation.error : undefined}
        onConfirm={() => approveMutation.mutate()}
      />

      <RejectModal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        permitUuid={permitUuid}
        department={department}
        onDone={invalidate}
      />
    </div>
  );
}

interface RejectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  department: PermitApprovalDepartment;
  onDone: () => void;
}

function RejectModal({ open, onOpenChange, permitUuid, department, onDone }: RejectModalProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({ reason: z.string().trim().min(1, t('departmentReview.reject.reasonRequired')) }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      departmentReviewApi.rejectDepartmentReview(permitUuid, {
        department: department,
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      onDone();
      reset();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={t('departmentReview.reject.title')}
      description={t('departmentReview.reject.description')}
      size="sm"
      closeLabel={t('permits.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permits.common.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="department-reject-form"
            loading={mutation.isPending}
          >
            {mutation.isPending ? (
              <Spinner label={t('departmentReview.reject.confirm')} />
            ) : (
              t('departmentReview.reject.confirm')
            )}
          </Button>
        </>
      }
    >
      <form
        id="department-reject-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <Field
          htmlFor="department-reject-reason"
          label={t('departmentReview.reject.field.reason')}
          error={formState.errors.reason?.message}
          required
        >
          <Textarea
            id="department-reject-reason"
            invalid={Boolean(formState.errors.reason)}
            placeholder={t('departmentReview.reject.reasonPlaceholder')}
            {...register('reason')}
          />
        </Field>
      </form>
    </Modal>
  );
}
